import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, IconButton, Paper, Typography } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import NewReleasesIcon from "@material-ui/icons/NewReleases";
import CloseIcon from "@material-ui/icons/Close";
import { MenuMessages } from "@shared/languages/interfaces";
import { useAuth } from "@hooks/useAuth";
import { useSeenVersion } from "@hooks/useSeenVersion";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // fixed just below the app bar, over the page, until the user acts on it
    banner: {
      position: "fixed",
      top: 76,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 1150,
      // as wide as its content, so the text stays on one line; never wider than the screen
      width: "max-content",
      maxWidth: "calc(100vw - 24px)",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.25, 1, 1.25, 2),
      borderRadius: 10,
      borderLeft: "5px solid #ffcc00",
      boxShadow: "0 6px 24px rgba(0, 41, 81, 0.22)",
      animation: "$enter .25s ease-out",
      [theme.breakpoints.down("xs")]: {
        flexWrap: "wrap"
      }
    },
    "@keyframes enter": {
      from: { opacity: 0, transform: "translate(-50%, -8px)" },
      to: { opacity: 1, transform: "translate(-50%, 0)" }
    },
    icon: {
      color: "#002951",
      fontSize: 30,
      flex: "none"
    },
    text: {
      flex: 1,
      minWidth: 180,
      fontSize: 15,
      fontWeight: 500,
      color: "#212121",
      whiteSpace: "nowrap",
      [theme.breakpoints.down("xs")]: {
        whiteSpace: "normal"
      }
    },
    actions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
      marginLeft: "auto",
      flex: "none"
    },
    see: {
      backgroundColor: "#002951",
      color: "#fff",
      textTransform: "none",
      fontWeight: 600,
      "&:hover": {
        backgroundColor: "#0f3a6e"
      }
    },
    dismiss: {
      textTransform: "none",
      color: "#002951"
    }
  })
);

/**
 * Banner shown once per version line (1.109, 1.110…) to each user: "Nova versão! Veja o que há de novo".
 * Ver novidades opens the version in Documentação › Novidades; either button marks it as seen, and the mark
 * is kept per user in the browser. Hotfixes keep the line, so they never show it.
 */
const NewVersionBanner: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const { version, unseen, markSeen } = useSeenVersion(user?.id_user);

  // the Documentação page shows the Novidades itself
  if (!unseen || location.pathname.startsWith("/docs")) return null;

  const openNews = () => {
    markSeen();
    history.push(`/docs?novidades=${encodeURIComponent(version)}`);
  };

  return (
    <Paper className={classes.banner} role="status" aria-live="polite" elevation={0}>
      <NewReleasesIcon className={classes.icon} />
      <Typography className={classes.text}>{t(MenuMessages.newVersionBanner, { version })}</Typography>
      <div className={classes.actions}>
        <Button variant="contained" disableElevation className={classes.see} onClick={openNews}>
          {t(MenuMessages.newVersionSee)}
        </Button>
        <Button className={classes.dismiss} onClick={markSeen}>
          {t(MenuMessages.newVersionDismiss)}
        </Button>
        <IconButton size="small" aria-label={t(MenuMessages.newVersionDismiss)} onClick={markSeen}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
    </Paper>
  );
};

export default NewVersionBanner;
